/**
 * Phase 14.4 — Explorer two-column flow chart (Domestic | International).
 *
 * Splits `/api/explorer/{origin_iata}` destinations into two side-by-side
 * columns. Domestic (same country as origin) renders on the LEFT;
 * international on the RIGHT. The connector hairline sits between each
 * destination box and the central origin axis; the flight price (blue)
 * always flanks the side FACING the origin, the package price (purple)
 * flanks the side AWAY from origin.
 *
 * Mobile narrow widths (<640px) stack the columns vertically — domestic
 * first, then international — handled by Tailwind's `sm:` breakpoint.
 */
import CategoryGlyph from "@/components/CategoryGlyph";

const PRICE_BLUE = "#3B82F6";
const PRICE_PURPLE = "#A855F7";
const HOTEL_GREEN = "#10B981";
const INK = "#0A0A0A";
const RULE = "#E5E5E5";

const CURRENCY_SYM = {
    INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ", SGD: "S$",
    JPY: "¥", IDR: "Rp", CHF: "Fr", THB: "฿",
};

/** Fallback set of IN airports — used only when neither `scope` nor
 *  `country` are present on the destination payload. */
const IN_IATA = new Set([
    "DEL", "BOM", "BLR", "MAA", "CCU", "HYD", "GOI", "JAI", "COK", "IXL",
    "IXZ", "AMD", "PNQ", "IXC", "TRV", "ATQ", "BBI", "VNS", "IXB", "IDR",
    "LKO", "NAG", "PAT", "RPR", "UDR", "VTZ", "IXA", "IXR", "IXM", "IXJ",
    "IXE", "IXG", "IXS", "IXU", "DIB", "GAU", "GAY", "VGA", "HBX", "RAJ",
    "DED", "JLR", "SHL", "TEZ", "TIR",
]);

function fmt(amt, ccy) {
    if (amt == null) return null;
    const sym = CURRENCY_SYM[(ccy || "USD").toUpperCase()] || "";
    return `${sym}${Math.round(amt).toLocaleString()}`;
}
function flightLabel(d)  { return d?.flight?.min  != null ? fmt(d.flight.min, d.flight.currency)   : null; }
function packageLabel(d) {
    if (d?.package?.price == null) return null;
    const base = fmt(d.package.price, d.package.currency);
    return d.package.duration_days ? `${base} (${d.package.duration_days}d)` : base;
}
function hotelLabel(d) {
    if (d?.hotel?.min == null) return null;
    const lo = fmt(d.hotel.min, d.hotel.currency);
    if (d.hotel.max && d.hotel.max !== d.hotel.min) {
        return `${lo} – ${fmt(d.hotel.max, d.hotel.currency)}/n`;
    }
    return `${lo}/n`;
}

function isDomestic(d, origin) {
    if (d?.scope === "domestic") return true;
    if (d?.scope === "international") return false;
    if (d?.country && origin?.country) return d.country === origin.country;
    return d?.iata ? IN_IATA.has(d.iata) : false;
}

export default function ExplorerAnimation({ countryCode, destinations, origin, onPick }) {
    const originLabel = origin?.city || origin?.iata || countryCode || "Origin";
    const list = Array.isArray(destinations) ? destinations : [];
    const domestic = list.filter((d) => isDomestic(d, origin));
    const international = list.filter((d) => !isDomestic(d, origin));

    return (
        <div
            className="explorer-flowchart mx-auto flex w-full max-w-[820px] flex-col items-center px-2"
            data-testid="explorer-flowchart"
            data-country={countryCode}
        >
            <div
                className="mb-0.5 text-[10px] uppercase tracking-[0.18em] text-[#A3A3A3] sm:mb-1"
                data-testid="explorer-origin-eyebrow"
            >
                From
            </div>
            <div
                className="mb-3 text-[14px] font-semibold tracking-tight text-[#0A0A0A] sm:mb-5 sm:text-[16px]"
                data-testid="explorer-origin-label"
            >
                {originLabel}
            </div>

            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-start sm:gap-6">
                <Column title="DOMESTIC" items={domestic} side="left" onPick={onPick} />
                <Column title="INTERNATIONAL" items={international} side="right" onPick={onPick} />
            </div>

            <style>{`
                /* Phase 14.4 animation polish — connector → prices → hotel,
                   each chained off a shared --fc-delay CSS var (idx * 80ms). */
                .explorer-flowchart .fc-connector {
                    transform: scaleY(0);
                    transform-origin: top center;
                    animation: fc-conn 260ms cubic-bezier(.2,.7,.3,1) forwards;
                    animation-delay: var(--fc-delay, 0ms);
                }
                .explorer-flowchart .fc-price {
                    opacity: 0;
                    transform: translateY(3px);
                    animation: fc-fade 320ms cubic-bezier(.2,.7,.3,1) forwards;
                    animation-delay: calc(var(--fc-delay, 0ms) + 220ms);
                }
                .explorer-flowchart .fc-box {
                    opacity: 0;
                    transform: translateY(4px);
                    animation: fc-fade 320ms cubic-bezier(.2,.7,.3,1) forwards;
                    animation-delay: calc(var(--fc-delay, 0ms) + 260ms);
                }
                .explorer-flowchart .fc-hotel {
                    opacity: 0;
                    animation: fc-fade 240ms cubic-bezier(.2,.7,.3,1) forwards;
                    animation-delay: calc(var(--fc-delay, 0ms) + 500ms);
                }
                @keyframes fc-conn { to { transform: scaleY(1); } }
                @keyframes fc-fade { to { opacity: 1; transform: translateY(0); } }
                @media (prefers-reduced-motion: reduce) {
                    .explorer-flowchart .fc-connector,
                    .explorer-flowchart .fc-price,
                    .explorer-flowchart .fc-box,
                    .explorer-flowchart .fc-hotel {
                        opacity: 1; transform: none; animation: none;
                    }
                }
            `}</style>
        </div>
    );
}

function Column({ title, items, side, onPick }) {
    if (items.length === 0) return null;
    return (
        <div
            className="flex flex-1 flex-col items-center sm:max-w-[50%]"
            data-testid={`explorer-column-${side}`}
        >
            <div
                className="mb-1.5 rounded-full border border-[#E5E5E5] bg-[#F5F5F5] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#737373] sm:mb-3 sm:px-2.5 sm:text-[10px]"
                data-testid={`explorer-column-title-${side}`}
            >
                {title}
            </div>
            {items.map((d, idx) => (
                <DestinationBlock key={d.iata || idx} d={d} idx={idx} side={side} onPick={onPick} />
            ))}
        </div>
    );
}

function DestinationBlock({ d, idx, side, onPick }) {
    const flight = flightLabel(d);
    const pkg = packageLabel(d);
    const hotel = hotelLabel(d);
    // Phase 15 layout — the connector reads like a labelled flowchart
    // edge: FLIGHT price sits ABOVE the line, PACKAGE price BELOW the
    // line, and the HOTEL price lives AT the destination node itself.
    return (
        <div
            className="mb-1 flex w-full flex-col items-center sm:mb-2"
            data-testid={`explorer-dest-${d.iata || idx}`}
            data-iata={d.iata}
            style={{ "--fc-delay": `${idx * 80}ms` }}
        >
            <span
                className="fc-price text-center text-[10px] font-medium tabular-nums sm:text-[11px]"
                style={{ color: PRICE_BLUE }}
                data-testid={`explorer-flight-${d.iata || idx}`}
            >
                {flight ? `✈ ${flight}` : "\u00A0"}
            </span>
            <span
                aria-hidden
                className="fc-connector my-0.5 inline-block h-px w-16 sm:w-24"
                style={{ background: RULE }}
            />
            <span
                className="fc-price text-center text-[10px] font-medium tabular-nums sm:text-[11px]"
                style={{ color: PRICE_PURPLE }}
                data-testid={`explorer-pkg-${d.iata || idx}`}
            >
                {pkg ? `Package ${pkg}` : "\u00A0"}
            </span>
            <button
                type="button"
                onClick={() => onPick?.(d.city || d.iata)}
                disabled={!onPick}
                className={`fc-box mt-0.5 flex max-w-[220px] flex-col items-center gap-0.5 rounded-md border border-[#0A0A0A]/85 bg-white px-2 py-1 text-left transition sm:mt-1 sm:px-2.5 sm:py-1.5 ${onPick ? "cursor-pointer hover:-translate-y-px hover:shadow-sm" : "cursor-default"}`}
                style={{ color: INK }}
                data-testid={`explorer-dest-box-${d.iata || idx}`}
            >
                <div className="flex items-center gap-1 sm:gap-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] sm:text-[12px] sm:tracking-[0.1em]">
                        {d.city || d.iata}
                    </span>
                    {d.iata && (
                        <span
                            className="rounded-full border border-[#E5E5E5] bg-[#F5F5F5] px-1 py-0.5 text-[10px] font-semibold tracking-wider text-[#525252] sm:px-1.5 sm:text-[10px]"
                            data-testid={`explorer-iata-chip-${d.iata}`}
                        >
                            {d.iata}
                        </span>
                    )}
                </div>
                {d.vibe && (
                    <span className="inline-flex items-center gap-1">
                        <CategoryGlyph name={d.vibe} size={10} color="#A3A3A3" />
                        <span
                            className="text-[10px] italic tracking-wide text-[#A3A3A3] sm:text-[10px]"
                            data-testid={`explorer-vibe-${d.iata || idx}`}
                        >
                            {String(d.vibe).toUpperCase()}
                        </span>
                    </span>
                )}
                <span
                    className="fc-hotel text-[10px] font-medium tabular-nums sm:text-[10px]"
                    style={{ color: hotel ? HOTEL_GREEN : "transparent" }}
                    data-testid={`explorer-hotel-${d.iata || idx}`}
                >
                    {hotel ? `Hotels ${hotel}` : "\u00A0"}
                </span>
            </button>
        </div>
    );
}
